import axios from 'axios';
import { Logger } from '../Logger';
import { Ticket } from '../../common-types/Ticket.type';

const Events = {
  EXPERIENCE_GAME_PLAYED: 'experience_game_played',
  EXPERIENCE_PAGE_VIEWED: 'experience_page_viewed',
};

interface GamePlayedEventInfo {
  ticket: Ticket,
  siteKey: string,
  shopperUid: string,
  cartUid: string,
}

interface PageDisplayedEventInfo {
  ticket: Ticket | undefined,
  siteKey: string,
  shopperUid: string,
}

class EventService {
  private readonly host: string;

  public constructor () {
    this.host = process.env.REACT_APP_EVENT_BASE_URL as string;
  }

  /**
   * Send event when a new page of the experience is loaded
   * @param {string} page
   * @param {PageDisplayedEventInfo} eventInfo
   * @returns {Promise<void>}
   */
  public sendPageDisplayedEvent (page: string, eventInfo: PageDisplayedEventInfo): void {
    const data = {
      campaignOid: eventInfo.ticket?.camp,
      cartOid: eventInfo.ticket?.cart,
      date: new Date(),
      shopperOid: eventInfo.ticket?.cust,
      shopperUid: eventInfo.shopperUid,
      siteKey: eventInfo.siteKey,
      siteOid: eventInfo.ticket?.site,
      ticketCode: eventInfo.ticket?.code,
      // eslint-disable-next-line no-underscore-dangle
      ticketOid: eventInfo.ticket?._id,
    };
    const payload = {
      page,
    };
    this.sendEvent(Events.EXPERIENCE_PAGE_VIEWED, data, payload);
  }

  /**
   * Send an event when the game has been played and the result has been returned
   * @param {boolean} hasWon
   * @param {GamePlayedEventInfo} eventInfo
   * @returns {Promise<void>}
   */
  public sendGamePlayedEvent (hasWon: boolean, eventInfo: GamePlayedEventInfo): void {
    const data = {
      campaignOid: eventInfo.ticket.camp,
      cartOid: eventInfo.ticket.cart,
      date: new Date(),
      shopperOid: eventInfo.ticket.cust,
      shopperUid: eventInfo.shopperUid,
      siteKey: eventInfo.siteKey,
      siteOid: eventInfo.ticket.site,
      ticketCode: eventInfo.ticket.code,
      // eslint-disable-next-line no-underscore-dangle
      ticketOid: eventInfo.ticket._id,
    };
    const payload = {
      hasWon,
    };
    this.sendEvent(Events.EXPERIENCE_GAME_PLAYED, data, payload);
  }

  /**
   * Send an event
   * @param {string} eventName
   * @param {Object} data
   * @param {Object} payload - free form object
   * @returns {Promise<void>}
   */
  private sendEvent (eventName: string, data: object, payload: object): void {
    const eventUrl = `${this.host}/events/v2/${eventName}`;
    const body = {
      ...data,
      ...payload,
    };
    axios.post(eventUrl, body).catch((err) => {
      Logger.log(`Couldn't send the event ${eventName}`, err);
    });
  }
}

export const eventServiceInstance = new EventService();
